import type { Currency } from '../../../types'

/**
 * Částky chodí ze serveru jako celá čísla v nejmenších jednotkách měny.
 * Tento modul je jediné místo, kde se převádějí na text a zpět.
 */

const LOCALE = 'cs-CZ'

const formatterCache = new Map<string, Intl.NumberFormat>()

function currencyFormatter(currencyCode: string, exponent: number): Intl.NumberFormat {
    const key = `${currencyCode}:${exponent}`
    let formatter = formatterCache.get(key)

    if (!formatter) {
        formatter = new Intl.NumberFormat(LOCALE, {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: exponent,
            maximumFractionDigits: exponent,
        })
        formatterCache.set(key, formatter)
    }

    return formatter
}

export function exponentOf(currencies: Currency[] | undefined, currencyCode: string): number {
    return currencies?.find((currency) => currency.code === currencyCode)?.exponent ?? 2
}

/** Zformátuje částku včetně měny, např. „1 234,50 Kč". */
export function formatMoney(minorAmount: number, currencyCode: string, exponent = 2): string {
    return currencyFormatter(currencyCode, exponent).format(minorAmount / 10 ** exponent)
}

/** Zformátuje částku bez symbolu měny - do vstupních polí a tabulek. */
export function formatAmount(minorAmount: number, exponent = 2): string {
    return new Intl.NumberFormat(LOCALE, { minimumFractionDigits: exponent, maximumFractionDigits: exponent }).format(
        minorAmount / 10 ** exponent,
    )
}

/**
 * Převede text z formuláře na nejmenší jednotky měny.
 * Přijímá desetinnou čárku i tečku a mezery jako oddělovač tisíců.
 */
export function parseAmount(text: string, exponent = 2): number | undefined {
    const normalized = text.replace(/\s| /g, '').replace(',', '.')
    if (normalized === '') {
        return undefined
    }

    const value = Number(normalized)
    if (!Number.isFinite(value)) {
        return undefined
    }

    // Zaokrouhlení od nuly, aby 1,005 nespadlo na 1,00 kvůli plovoucí čárce.
    const scaled = value * 10 ** exponent
    return scaled < 0 ? -Math.round(Math.abs(scaled) + 1e-9) : Math.round(scaled + 1e-9)
}

/** Hodnota částky pro vstupní pole (bez oddělovačů tisíců). */
export function toAmountInputValue(minorAmount: number | undefined, exponent = 2): string {
    if (minorAmount === undefined) {
        return ''
    }
    return (minorAmount / 10 ** exponent).toFixed(exponent).replace('.', ',')
}

/** Kurz měny pro zobrazení, např. „24,350". */
export function formatRate(rate: number): string {
    return new Intl.NumberFormat(LOCALE, { minimumFractionDigits: 3, maximumFractionDigits: 4 }).format(rate)
}
